type ScheduleEventLike = Pick<ScheduleEvent, "id" | "status">
type WorkOrderLike = Pick<WorkOrder, "id" | "case_id">

export type AnnouncementPlanCreateStateReason =
  | "active_plan"
  | "allowed"
  | "completed"
  | "inactive"
  | "missing_context"
  | "missing_work_order"
  | "read_only"
  | "unsupported_status"

export type AnnouncementPlanCreateState = {
  actionBoxLabel: string
  canCreate: boolean
  message: string | null
  reason: AnnouncementPlanCreateStateReason
}

const CREATABLE_STATUSES = new Set(["RESERVED", "ANNOUNCED"])
const COMPLETED_STATUSES = new Set(["CONFIRMED", "COMPLETED"])
const INACTIVE_STATUSES = new Set(["CANCELED", "NOSHOW"])

const normalizeStatus = (status: unknown): string => `${status ?? ""}`.trim().toUpperCase()

export const resolveAnnouncementPlanCreateState = ({
  event,
  workOrder,
  activeOrder,
  forceReadOnly = false,
}: {
  event?: ScheduleEventLike | null
  workOrder?: WorkOrderLike | null
  activeOrder?: unknown | null
  forceReadOnly?: boolean
}): AnnouncementPlanCreateState => {
  if (activeOrder) {
    return {
      actionBoxLabel: "Aktiver Plan",
      canCreate: false,
      message: null,
      reason: "active_plan",
    }
  }

  if (forceReadOnly) {
    return {
      actionBoxLabel: "Plan nicht möglich",
      canCreate: false,
      message: "In dieser Ansicht kann kein Ankündigungsplan erstellt werden.",
      reason: "read_only",
    }
  }

  if (!workOrder?.case_id) {
    return {
      actionBoxLabel: "Plan nicht möglich",
      canCreate: false,
      message: "Für diesen Termin ist kein Vorgang verknüpft.",
      reason: "missing_work_order",
    }
  }

  const status = normalizeStatus(event?.status)

  if (COMPLETED_STATUSES.has(status)) {
    return {
      actionBoxLabel: "Plan nicht nötig",
      canCreate: false,
      message: "Termin ist bereits bestätigt; kein Ankündigungsplan nötig.",
      reason: "completed",
    }
  }

  if (INACTIVE_STATUSES.has(status)) {
    return {
      actionBoxLabel: "Plan nicht möglich",
      canCreate: false,
      message:
        "Termin ist nicht mehr aktiv; für einen neuen Termin bitte verschieben oder neu planen.",
      reason: "inactive",
    }
  }

  if (!event?.id || !workOrder.id) {
    return {
      actionBoxLabel: "Plan nicht möglich",
      canCreate: false,
      message: "Für diesen Termin fehlen noch die nötigen Auftragsdaten.",
      reason: "missing_context",
    }
  }

  if (!CREATABLE_STATUSES.has(status)) {
    return {
      actionBoxLabel: "Plan nicht möglich",
      canCreate: false,
      message: "Für diesen Terminstatus kann kein Ankündigungsplan erstellt werden.",
      reason: "unsupported_status",
    }
  }

  return {
    actionBoxLabel: "Plan anlegen",
    canCreate: true,
    message: null,
    reason: "allowed",
  }
}
